/**
 * "Set the same options for every file."
 *
 * The control that makes a multi-file basket worth having: ten lecture notes
 * all want double-sided black and white, and setting that ten times is the
 * work this screen was supposed to remove.
 *
 * THREE DECISIONS WORTH KEEPING.
 *
 * **Every control defaults to "leave them as they are."** A sheet that
 * arrived pre-filled with something would quietly overwrite the one file the
 * customer had already set up by hand. Nothing is sent that was not chosen
 * here, which is exactly what the endpoint's "absent means unchanged" means.
 *
 * **It shows unit prices, never totals.** A per-page rate and a per-copy
 * binding charge are facts about the option; "₹96" is a fact about one
 * document and would be a lie on the other nine. The real total updates
 * behind the sheet the moment the change lands.
 *
 * **A half-success stays on screen.** A binding only fits a book thin enough
 * to take it, so "spiral everything" can legitimately apply to three files
 * out of four. The server says which it skipped and why; the sheet stays open
 * and repeats it, because closing on a cheerful note would leave the customer
 * believing a 400-page report is bound.
 */
import { useState } from 'react';

import { Alert, Button, Checkbox, Modal, OptionCard, RadioGroup } from '@/components/ui';
import { CopiesField } from '@/features/pricing/CopiesField';
import { CoverColourPicker } from '@/features/pricing/CoverColourPicker';
import { updateAllCartItems } from '@/lib/api/cart';
import { useBindingPrices, useOptionPrices } from '@/lib/api/quotes';
import { useApiMutation } from '@/lib/api/queryClient';
import type {
  CartBulkResultOut,
  CartBulkUpdate,
  CoverColour,
  PrintOptionOut,
} from '@/lib/api/types';
import { formatPaise } from '@/lib/format';

/** Selected when nothing in that section should change. */
const UNCHANGED = '__unchanged__';
/** The binding section's "take the binding off everything" choice. */
const NO_BINDING = '__none__';

export interface ApplyToAllProps {
  open: boolean;
  onClose: () => void;
  /**
   * Any priceable file. Used ONLY to read the catalogue — the presets and
   * bindings the shop offers — because there is no endpoint that lists them
   * without a document to price them against. Nothing document-specific is
   * rendered from it.
   */
  referenceUploadId: string | undefined;
  coverColours: CoverColour[];
  /** How many files the change would touch, for the button's own words. */
  fileCount: number;
  /** Fired with the whole new cart, which becomes the cache. */
  onApplied: (result: CartBulkResultOut) => void;
}

/** The server sends a code for a file it could not touch, or its own sentence. */
function skipReason(reason: string): string {
  switch (reason) {
    case 'validating':
      return 'still being checked';
    case 'rejected':
      return 'cannot be printed';
    case 'no_print_option':
      return 'has nothing to print yet';
    default:
      return reason;
  }
}

export function ApplyToAll({
  open,
  onClose,
  referenceUploadId,
  coverColours,
  fileCount,
  onApplied,
}: ApplyToAllProps) {
  const [printOption, setPrintOption] = useState<string>(UNCHANGED);
  const [binding, setBinding] = useState<string>(UNCHANGED);
  const [coverColour, setCoverColour] = useState<string | null>(null);
  const [setsCopies, setSetsCopies] = useState(false);
  const [copies, setCopies] = useState(1);
  const [result, setResult] = useState<CartBulkResultOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Priced against one file, but only the catalogue is read off them: the
  // per-page rate and the per-copy binding charge are properties of the
  // option, not of the document they happen to have been priced for.
  const optionPrices = useOptionPrices({
    uploadId: open ? referenceUploadId : undefined,
    copies: 1,
  });
  const bindingPrices = useBindingPrices({
    uploadId: open ? referenceUploadId : undefined,
    copies: 1,
    printOptionSlug: optionPrices.data?.options[0]?.option.slug,
  });

  const bindings = (bindingPrices.data?.bindings ?? []).map((entry) => entry.binding);
  const chosenBinding = bindings.find((entry) => entry.slug === binding);

  const patch: CartBulkUpdate = {
    ...(printOption !== UNCHANGED ? { print_option_slug: printOption } : {}),
    ...(binding === NO_BINDING ? { clear_binding: true } : {}),
    ...(binding !== UNCHANGED && binding !== NO_BINDING ? { binding_slug: binding } : {}),
    ...(chosenBinding?.has_cover && coverColour ? { cover_colour: coverColour } : {}),
    ...(setsCopies ? { copies } : {}),
  };
  const changesSomething = Object.keys(patch).length > 0;

  const apply = useApiMutation<CartBulkResultOut, CartBulkUpdate>({
    mutationFn: (body) => updateAllCartItems(body),
    onSuccess: (applied) => {
      setError(null);
      onApplied(applied);
      // Nothing to report means nothing to stay open for.
      if (applied.skipped.length === 0) {
        close();
        return;
      }
      setResult(applied);
    },
    onError: (failure) => setError(failure.message),
  });

  function close() {
    setPrintOption(UNCHANGED);
    setBinding(UNCHANGED);
    setCoverColour(null);
    setSetsCopies(false);
    setCopies(1);
    setResult(null);
    setError(null);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Set options for every file"
      description={`Anything you choose here replaces that setting on all ${fileCount} files. Leave a section alone and it stays as it is.`}
      footer={
        <div className="flex gap-3">
          <Button variant="secondary" fullWidth onClick={close}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          <Button
            fullWidth
            disabled={!changesSomething || apply.isPending}
            loading={apply.isPending}
            onClick={() => apply.mutate(patch)}
          >
            Apply to all
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {result ? (
          <Alert severity="warning" title="Some files kept their own settings" live>
            <ul className="list-disc space-y-1 pl-5">
              {result.skipped.map((skip) => (
                <li key={skip.item_id}>
                  <span className="font-medium">{skip.filename}</span> — {skipReason(skip.reason)}.
                </li>
              ))}
            </ul>
            <p className="mt-2">
              {result.applied_item_ids.length === 0
                ? 'Nothing else changed.'
                : `The other ${result.applied_item_ids.length} ${
                    result.applied_item_ids.length === 1 ? 'file was' : 'files were'
                  } updated.`}
            </p>
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="danger" title="We could not apply that" live>
            <p>{error}</p>
          </Alert>
        ) : null}

        <RadioGroup
          legend="How should we print them?"
          hint="Per page. What each file costs depends on how many pages it has."
        >
          <OptionCard
            name="apply-all-preset"
            value={UNCHANGED}
            checked={printOption === UNCHANGED}
            onChange={() => setPrintOption(UNCHANGED)}
            label="Leave as they are"
          />
          {optionPrices.data?.options.map(({ option }: { option: PrintOptionOut }) => (
            <OptionCard
              key={option.id}
              name="apply-all-preset"
              value={option.slug}
              checked={printOption === option.slug}
              onChange={() => setPrintOption(option.slug)}
              label={option.label}
              description={option.description ?? undefined}
              meta={`${formatPaise(option.price_per_page_paise)} per page`}
            />
          ))}
        </RadioGroup>

        <div className="space-y-3">
          <Checkbox
            name="apply-all-copies"
            checked={setsCopies}
            onChange={(event) => setSetsCopies(event.target.checked)}
            label="Print the same number of copies of each"
          />
          {setsCopies ? <CopiesField value={copies} onChange={setCopies} /> : null}
        </div>

        {bindings.length > 0 ? (
          <RadioGroup
            legend="How should they be bound?"
            hint="Per copy. A file too thick or too thin for the binding keeps its own setting, and we will say which."
          >
            <OptionCard
              name="apply-all-binding"
              value={UNCHANGED}
              checked={binding === UNCHANGED}
              onChange={() => setBinding(UNCHANGED)}
              label="Leave as they are"
            />
            <OptionCard
              name="apply-all-binding"
              value={NO_BINDING}
              checked={binding === NO_BINDING}
              onChange={() => setBinding(NO_BINDING)}
              label="No binding"
              description="Loose sheets, held with a clip."
            />
            {bindings.map((entry) => (
              <OptionCard
                key={entry.id}
                name="apply-all-binding"
                value={entry.slug}
                checked={binding === entry.slug}
                onChange={() => setBinding(entry.slug)}
                label={entry.label}
                description={entry.description ?? undefined}
                meta={entry.price_paise > 0 ? `${formatPaise(entry.price_paise)} per copy` : 'Free'}
              />
            ))}
          </RadioGroup>
        ) : null}

        {chosenBinding?.has_cover ? (
          <CoverColourPicker
            colours={coverColours}
            value={coverColour}
            onChange={setCoverColour}
            bindingLabel={chosenBinding.label}
          />
        ) : null}
      </div>
    </Modal>
  );
}
